/* global axios */
import ApiClient from './ApiClient';

const buildResource = resource =>
  new ApiClient(`cortex/${resource}`, { accountScoped: true });

const extendResource = (resource, methods) => {
  const client = buildResource(resource);
  return Object.assign(client, methods(client));
};

export const cortexAgentsAPI = extendResource('agents', client => ({
  templates: () => axios.get(`${client.url}/templates`),
  wizard: data => axios.post(`${client.url}/wizard`, data),
  inboxes: (agentId, params = {}) =>
    axios.get(`${client.url}/${agentId}/inboxes`, { params }),
  connectInbox: (agentId, data) =>
    axios.post(`${client.url}/${agentId}/inboxes`, data),
  updateInbox: (agentId, inboxId, data) =>
    axios.patch(`${client.url}/${agentId}/inboxes/${inboxId}`, data),
  disconnectInbox: (agentId, inboxId) =>
    axios.delete(`${client.url}/${agentId}/inboxes/${inboxId}`),
  activateInbox: (agentId, inboxId) =>
    axios.post(`${client.url}/${agentId}/inboxes/${inboxId}/activate`),
  deactivateInbox: (agentId, inboxId) =>
    axios.post(`${client.url}/${agentId}/inboxes/${inboxId}/deactivate`),
  tools: agentId => axios.get(`${client.url}/${agentId}/tools`),
  connectTool: (agentId, data) =>
    axios.post(`${client.url}/${agentId}/tools`, data),
  disconnectTool: (agentId, toolId) =>
    axios.delete(`${client.url}/${agentId}/tools/${toolId}`),
  scenarios: agentId => axios.get(`${client.url}/${agentId}/scenarios`),
  createScenario: (agentId, data) =>
    axios.post(`${client.url}/${agentId}/scenarios`, data),
  showScenario: (agentId, scenarioId) =>
    axios.get(`${client.url}/${agentId}/scenarios/${scenarioId}`),
  updateScenario: (agentId, scenarioId, data) =>
    axios.patch(`${client.url}/${agentId}/scenarios/${scenarioId}`, data),
  deleteScenario: (agentId, scenarioId) =>
    axios.delete(`${client.url}/${agentId}/scenarios/${scenarioId}`),
  playgroundRuns: agentId =>
    axios.get(`${client.url}/${agentId}/playground_runs`),
  createPlaygroundRun: (agentId, data) =>
    axios.post(`${client.url}/${agentId}/playground_runs`, data),
  showPlaygroundRun: (agentId, runId) =>
    axios.get(`${client.url}/${agentId}/playground_runs/${runId}`),
}));

export const cortexPlaygroundRunsAPI = extendResource(
  'playground_runs',
  client => ({
    train: (runId, data) => axios.post(`${client.url}/${runId}/train`, data),
    retest: runId => axios.post(`${client.url}/${runId}/retest`),
  })
);

export const cortexKnowledgeBasesAPI = extendResource(
  'knowledge_bases',
  client => ({
    sources: (knowledgeBaseId, params = {}) =>
      axios.get(`${client.url}/${knowledgeBaseId}/sources`, { params }),
    createSource: (knowledgeBaseId, data) =>
      axios.post(`${client.url}/${knowledgeBaseId}/sources`, { source: data }),
    updateSource: (knowledgeBaseId, sourceId, data) =>
      axios.patch(`${client.url}/${knowledgeBaseId}/sources/${sourceId}`, {
        source: data,
      }),
    deleteSource: (knowledgeBaseId, sourceId) =>
      axios.delete(`${client.url}/${knowledgeBaseId}/sources/${sourceId}`),
    syncSource: (knowledgeBaseId, sourceId) =>
      axios.post(`${client.url}/${knowledgeBaseId}/sources/${sourceId}/sync`),
    pauseSource: (knowledgeBaseId, sourceId) =>
      axios.post(`${client.url}/${knowledgeBaseId}/sources/${sourceId}/pause`),
    resumeSource: (knowledgeBaseId, sourceId) =>
      axios.post(`${client.url}/${knowledgeBaseId}/sources/${sourceId}/resume`),
    cancelSource: (knowledgeBaseId, sourceId) =>
      axios.post(`${client.url}/${knowledgeBaseId}/sources/${sourceId}/cancel`),
    retrySource: (knowledgeBaseId, sourceId, data = {}) =>
      axios.post(`${client.url}/${knowledgeBaseId}/sources/${sourceId}/retry`, {
        source: data,
      }),
    renormalizeSource: (knowledgeBaseId, sourceId, data) =>
      axios.post(
        `${client.url}/${knowledgeBaseId}/sources/${sourceId}/renormalize`,
        data
      ),
    previewSource: (knowledgeBaseId, sourceId, params = {}) =>
      axios.get(
        `${client.url}/${knowledgeBaseId}/sources/${sourceId}/preview`,
        {
          params,
        }
      ),
    reviewSource: (knowledgeBaseId, sourceId, data) =>
      axios.post(
        `${client.url}/${knowledgeBaseId}/sources/${sourceId}/review`,
        data
      ),
    syncHelpCenter: (knowledgeBaseId, data) =>
      axios.post(
        `${client.url}/${knowledgeBaseId}/sources/sync_help_center`,
        data
      ),
    previewStructured: (knowledgeBaseId, data) =>
      axios.post(
        `${client.url}/${knowledgeBaseId}/sources/preview_structured`,
        data
      ),
    importStructured: (knowledgeBaseId, data) =>
      axios.post(
        `${client.url}/${knowledgeBaseId}/sources/import_structured`,
        data
      ),
    documents: (knowledgeBaseId, params = {}) =>
      axios.get(`${client.url}/${knowledgeBaseId}/documents`, { params }),
    previewDocument: (knowledgeBaseId, data) =>
      axios.post(`${client.url}/${knowledgeBaseId}/documents/preview`, data),
    createDocument: (knowledgeBaseId, data) =>
      axios.post(`${client.url}/${knowledgeBaseId}/documents`, data),
    showDocument: (knowledgeBaseId, documentId, params = {}) =>
      axios.get(`${client.url}/${knowledgeBaseId}/documents/${documentId}`, {
        params,
      }),
    updateDocument: (knowledgeBaseId, documentId, data) =>
      axios.patch(
        `${client.url}/${knowledgeBaseId}/documents/${documentId}`,
        data
      ),
    deleteDocument: (knowledgeBaseId, documentId) =>
      axios.delete(`${client.url}/${knowledgeBaseId}/documents/${documentId}`),
  })
);

export const cortexConversationControlsAPI = extendResource(
  'conversations',
  client => ({
    status: conversationId =>
      axios.get(`${client.url}/${conversationId}/status`),
    runtimeStatus: conversationId =>
      axios.get(`${client.url}/${conversationId}/runtime_status`),
    runtimeReadiness: conversationId =>
      axios.get(`${client.url}/${conversationId}/runtime_readiness`),
    runtimeDecision: conversationId =>
      axios.get(`${client.url}/${conversationId}/runtime_decision`),
    runtimeRun: (conversationId, data = {}) =>
      axios.post(`${client.url}/${conversationId}/runtime_run`, data),
    replySuggestion: conversationId =>
      axios.post(`${client.url}/${conversationId}/reply_suggestion`),
    cortexChatStatus: (conversationId, params = {}) =>
      axios.get(`${client.url}/${conversationId}/cortex_chat_status`, {
        params,
      }),
    refineReplySuggestion: (conversationId, data) =>
      axios.post(
        `${client.url}/${conversationId}/reply_suggestion_refine`,
        data
      ),
    transcribeInstruction: (conversationId, formData) =>
      axios.post(
        `${client.url}/${conversationId}/transcribe_instruction`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
        }
      ),
    processCortexChatImage: (conversationId, formData) =>
      axios.post(
        `${client.url}/${conversationId}/process_cortex_chat_image`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
        }
      ),
    approveReplySuggestion: (conversationId, data) =>
      axios.post(
        `${client.url}/${conversationId}/approve_reply_suggestion`,
        data
      ),
    abandonReplySuggestion: (conversationId, data) =>
      axios.post(
        `${client.url}/${conversationId}/abandon_reply_suggestion`,
        data
      ),
    resolveReplySuggestion: (conversationId, data) =>
      axios.post(
        `${client.url}/${conversationId}/resolve_reply_suggestion`,
        data
      ),
    replySuggestionFeedback: (conversationId, data) =>
      axios.post(
        `${client.url}/${conversationId}/reply_suggestion_feedback`,
        data
      ),
    pause: conversationId =>
      axios.post(`${client.url}/${conversationId}/pause`),
    resume: conversationId =>
      axios.post(`${client.url}/${conversationId}/resume`),
    handoff: (conversationId, data = {}) =>
      axios.post(`${client.url}/${conversationId}/handoff`, data),
  })
);

export const cortexProcessingRunsAPI = extendResource(
  'processing_runs',
  client => ({
    list: (params = {}) => axios.get(client.url, { params }),
    show: (runId, params = {}) =>
      axios.get(`${client.url}/${runId}`, { params }),
    preview: data =>
      axios.post(`${client.url}/preview`, { processing_run: data }),
    start: (data, idempotencyKey) =>
      axios.post(
        client.url,
        { processing_run: data },
        { headers: { 'Idempotency-Key': idempotencyKey } }
      ),
    cancel: (runId, data, idempotencyKey) =>
      axios.post(
        `${client.url}/${runId}/cancel`,
        { processing_run: data },
        { headers: { 'Idempotency-Key': idempotencyKey } }
      ),
    resume: (runId, data, idempotencyKey) =>
      axios.post(
        `${client.url}/${runId}/resume`,
        { processing_run: data },
        { headers: { 'Idempotency-Key': idempotencyKey } }
      ),
    adjustBudget: (runId, data, idempotencyKey) =>
      axios.post(
        `${client.url}/${runId}/budget`,
        { processing_run: data },
        { headers: { 'Idempotency-Key': idempotencyKey } }
      ),
    media: (runId, params = {}) =>
      axios.get(`${client.url}/${runId}/media`, { params }),
    budgetAttempts: (runId, params = {}) =>
      axios.get(`${client.url}/${runId}/budget_attempts`, { params }),
    reconcileBudgetAttempt: (runId, attemptId, data, idempotencyKey) =>
      axios.post(
        `${client.url}/${runId}/budget_attempts/${attemptId}/reconcile`,
        { budget_attempt: data },
        { headers: { 'Idempotency-Key': idempotencyKey } }
      ),
  })
);

export const cortexLearningCandidatesAPI = extendResource(
  'learning_candidates',
  client => ({
    list: (params = {}) => axios.get(client.url, { params }),
    show: candidateId => axios.get(`${client.url}/${candidateId}`),
    triage: (candidateId, data, idempotencyKey) =>
      axios.post(
        `${client.url}/${candidateId}/triage`,
        { learning_candidate: data },
        { headers: { 'Idempotency-Key': idempotencyKey } }
      ),
    bulkTriage: (data, idempotencyKey) =>
      axios.post(
        `${client.url}/bulk_triage`,
        { learning_candidates: data },
        { headers: { 'Idempotency-Key': idempotencyKey } }
      ),
  })
);

export const cortexCurationProposalsAPI = extendResource(
  'curation_proposals',
  client => ({
    list: (params = {}) => axios.get(client.url, { params }),
    show: proposalId => axios.get(`${client.url}/${proposalId}`),
    updateDraft: (proposalId, data, idempotencyKey) =>
      axios.patch(
        `${client.url}/${proposalId}`,
        { curation_proposal: data },
        { headers: { 'Idempotency-Key': idempotencyKey } }
      ),
    prepare: proposalId => axios.post(`${client.url}/${proposalId}/prepare`),
    resumePublication: (proposalId, data, idempotencyKey) =>
      axios.post(
        `${client.url}/${proposalId}/resume_publication`,
        { curation_proposal: data },
        { headers: { 'Idempotency-Key': idempotencyKey } }
      ),
    approve: (proposalId, data, idempotencyKey) =>
      axios.post(
        `${client.url}/${proposalId}/approve`,
        { curation_proposal: data },
        { headers: { 'Idempotency-Key': idempotencyKey } }
      ),
    reject: (proposalId, data, idempotencyKey) =>
      axios.post(
        `${client.url}/${proposalId}/reject`,
        { curation_proposal: data },
        { headers: { 'Idempotency-Key': idempotencyKey } }
      ),
    bulkReview: (data, idempotencyKey) =>
      axios.post(
        `${client.url}/bulk_review`,
        { curation_proposals: data },
        { headers: { 'Idempotency-Key': idempotencyKey } }
      ),
  })
);

export const cortexEvaluationRunsAPI = extendResource(
  'evaluation_runs',
  client => ({
    list: (params = {}) => axios.get(client.url, { params }),
    show: runId => axios.get(`${client.url}/${runId}`),
    start: (data, idempotencyKey) =>
      axios.post(
        client.url,
        { evaluation_run: data },
        { headers: { 'Idempotency-Key': idempotencyKey } }
      ),
    cancel: (runId, data, idempotencyKey) =>
      axios.post(
        `${client.url}/${runId}/cancel`,
        { evaluation_run: data },
        { headers: { 'Idempotency-Key': idempotencyKey } }
      ),
  })
);

export const cortexToolsAPI = extendResource('tools', client => ({
  fromMacro: data => axios.post(`${client.url}/from_macro`, data),
  fromAutomation: data => axios.post(`${client.url}/from_automation`, data),
  test: data => axios.post(`${client.url}/test`, data),
  health: () => axios.get(`${client.url}/health`),
  monitoring: params => axios.get(`${client.url}/monitoring`, { params }),
}));

export const cortexToolExecutionsAPI = extendResource(
  'tool_executions',
  client => ({
    approve: (executionId, data = {}) =>
      axios.post(`${client.url}/${executionId}/approve`, data),
    reject: (executionId, data = {}) =>
      axios.post(`${client.url}/${executionId}/reject`, data),
  })
);

export const cortexConsentAPI = extendResource('consent', client => ({
  status: params => axios.get(`${client.url}/status`, { params }),
  legacyImportPreview: params =>
    axios.get(`${client.url}/legacy_import_preview`, { params }),
  accept: data => axios.post(`${client.url}/accept`, data),
  importLegacy: data => axios.post(`${client.url}/import_legacy`, data),
  refuse: data => axios.post(`${client.url}/refuse`, data),
  revoke: data => axios.post(`${client.url}/revoke`, data),
}));

export const cortexMemoryAPI = extendResource('memory', client => ({
  context: params => axios.get(`${client.url}/context`, { params }),
  purgeContact: contactId =>
    axios.delete(`${client.url}/purge_contact`, {
      params: { contact_id: contactId },
    }),
  updateConversation: data =>
    axios.patch(`${client.url}/update_conversation`, data),
  updateContact: data => axios.patch(`${client.url}/update_contact`, data),
}));

export const cortexMemoryFieldsAPI = extendResource(
  'memory_fields',
  client => ({
    ensure: (data = {}) => axios.post(`${client.url}/ensure`, data),
  })
);

export const cortexAuditEventsAPI = buildResource('audit_events');
export const cortexFirecrawlConfigurationAPI = extendResource(
  'firecrawl_configuration',
  client => ({
    update: data => axios.patch(client.url, { firecrawl_configuration: data }),
    test: () => axios.post(`${client.url}/test`),
  })
);
export const cortexUrlPolicyAPI = extendResource('url_policy', client => ({
  update: data => axios.patch(client.url, { url_policy: data }),
}));
export const cortexUrlCandidatesAPI = extendResource(
  'url_candidates',
  client => ({
    list: (params = {}) => axios.get(client.url, { params }),
    decide: (candidateId, data) =>
      axios.post(`${client.url}/${candidateId}/decide`, data),
  })
);
export const cortexUrlDomainPoliciesAPI = extendResource(
  'url_domain_policies',
  client => ({
    list: (params = {}) => axios.get(client.url, { params }),
    create: data => axios.post(client.url, { url_domain_policy: data }),
    update: (policyId, data) =>
      axios.patch(`${client.url}/${policyId}`, { url_domain_policy: data }),
  })
);
export const cortexMediaItemsAPI = extendResource('media_items', client => ({
  list: (params = {}) => axios.get(client.url, { params }),
  show: itemId => axios.get(`${client.url}/${itemId}`),
  updateProfile: (itemId, data) =>
    axios.patch(`${client.url}/${itemId}/profile`, data),
  normalize: itemId => axios.post(`${client.url}/${itemId}/normalize`),
  reprocess: itemId => axios.post(`${client.url}/${itemId}/reprocess`),
  review: (itemId, decision) =>
    axios.post(`${client.url}/${itemId}/review`, { decision }),
}));
export const cortexUsageAPI = extendResource('usage', client => ({
  get: params => axios.get(client.url, { params }),
}));
